import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import Drawer from '../../components/Drawer';
import {
  Badge, Banner, EmptyState, PageLoading, SearchInput, Select, TextInput,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import { ROLE_LABEL, initialsOf } from '../team/roleMeta';
import type { Area, CompanySettings, TeamMember } from '../../types';

const STATUS_TONE = {
  active: 'success',
  pending_approval: 'warning',
  archived: 'neutral',
} as const;

const STATUS_LABEL = {
  active: 'Active',
  pending_approval: 'Awaiting approval',
  archived: 'Archived',
} as const;

/** Lightweight shape returned by GET /routes/:id/assignments. */
interface AssignedUser {
  id: string;
  fullName: string;
  role: string;
  status: string;
  designation: string | null;
  employeeCode: string | null;
  avatarUrl: string | null;
  assignedAt: string;
}

/**
 * Build a flat list ordered as a depth-first tree: every parent is
 * immediately followed by its children (recursively). Nodes whose
 * parent is missing or filtered out are treated as roots so they
 * remain visible instead of disappearing.
 */
function treeOrder(areas: Area[]): Area[] {
  const byParent = new Map<string | null, Area[]>();
  const idSet = new Set(areas.map((a) => a.id));
  for (const a of areas) {
    // Treat as root if parent is not in the visible set
    const key = a.parentId && idSet.has(a.parentId) ? a.parentId : null;
    const list = byParent.get(key) ?? [];
    list.push(a);
    byParent.set(key, list);
  }
  const result: Area[] = [];
  function walk(parentId: string | null) {
    for (const a of byParent.get(parentId) ?? []) {
      result.push(a);
      walk(a.id);
    }
  }
  walk(null);
  return result;
}

/** Level name for a given depth, falling back to "Level N". */
function levelName(names: string[], depth: number): string {
  return names[depth] ?? `Level ${depth + 1}`;
}

export default function AreasPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [areas, setAreas] = useState<Area[] | null>(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Area | 'new' | null>(null);
  const [merging, setMerging] = useState<Area | null>(null);
  const [assigning, setAssigning] = useState<Area | null>(null);
  const [settings, setSettings] = useState<{ beatLevelNames: string[] } | null>(null);
  const [defaultParentId, setDefaultParentId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [routesData, settingsData] = await Promise.all([
        api.get<Area[]>('/customers/routes'),
        api.get<CompanySettings>('/settings'),
      ]);
      setAreas(routesData);
      setSettings({ beatLevelNames: settingsData.beatLevelNames ?? ['Area'] });
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load areas.');
      setAreas([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = (areas ?? [])
      .filter((area) => showArchived || area.status !== 'archived')
      .filter((area) => !needle
        || area.name.toLowerCase().includes(needle)
        || area.code?.toLowerCase().includes(needle)
        || area.city?.toLowerCase().includes(needle));
    return treeOrder(filtered);
  }, [areas, search, showArchived]);

  const pending = visible.filter((a) => a.status === 'pending_approval');

  async function approve(area: Area) {
    try {
      await api.patch(`/customers/routes/${area.id}`, { status: 'active' });
      toast.success(`${area.name} is now available to every rep.`);
      void load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not approve.');
    }
  }

  async function archive(area: Area) {
    try {
      await api.del(`/customers/routes/${area.id}`);
      toast.success(`${area.name} archived.`);
      void load();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'ROUTE_IN_USE') {
        toast.error(err.message);
      } else {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not archive.');
      }
    }
  }

  return (
    <>
      <PageHeader
        title="Areas / Routes"
        subtitle="The beats your reps work. Every customer belongs to one."
        actions={isAdmin ? (
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} /> New area
          </button>
        ) : undefined}
      />

      <div className="page">
        {pending.length ? (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="warning">
              <strong>
                {pending.length} area{pending.length === 1 ? '' : 's'} added by reps
                {pending.length === 1 ? ' is' : ' are'} waiting for you.
              </strong>{' '}
              A rep in the field can create an area so they are not blocked, but it stays
              off everyone else&apos;s list until you approve it.
            </Banner>
          </div>
        ) : null}

        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search areas" />
          <label className="switch">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span className="switch-track" />
            <span className="switch-label">Show archived</span>
          </label>
        </div>

        <div className="card">
          {!areas ? (
            <div className="card-pad"><PageLoading rows={3} /></div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon="map"
              title={search ? 'No areas match that' : 'No areas yet'}
              body={search
                ? 'Try a different name, code or city.'
                : 'Add the beats your reps work — a locality, a market, a highway stretch. Customers are grouped under them.'}
              action={isAdmin && !search ? (
                <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
                  <Icon name="plus" size={16} /> New area
                </button>
              ) : undefined}
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Area</th>
                    <th>Code</th>
                    <th>City</th>
                    <th>Customers</th>
                    <th>Distributor</th>
                    <th>Reps assigned</th>
                    <th>Status</th>
                    {isAdmin ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((area) => (
                    <tr key={area.id}>
                      <td className="primary">
                        <span style={{ paddingLeft: area.depth * 24, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {area.depth > 0 && <span className="faint" style={{ fontSize: 12 }}>↳</span>}
                          {area.name}
                        </span>
                      </td>
                      <td className="mono">{area.code ?? '—'}</td>
                      <td>{area.city ?? '—'}</td>
                      <td>{area.customerCount ?? 0}</td>
                      <td>
                        {area.distributor
                          ? <span>{area.distributor.name}</span>
                          : <span className="faint">—</span>}
                      </td>
                      <td>
                        {isAdmin ? (
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => setAssigning(area)}
                            title="Manage rep assignments"
                          >
                            {area.assignedReps
                              ? `${area.assignedReps} rep${area.assignedReps === 1 ? '' : 's'}`
                              : 'Everyone'}
                          </button>
                        ) : (
                          area.assignedReps
                            ? `${area.assignedReps} rep${area.assignedReps === 1 ? '' : 's'}`
                            : <span className="faint">Everyone</span>
                        )}
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[area.status]}>{STATUS_LABEL[area.status]}</Badge>
                      </td>
                      {isAdmin ? (
                        <td>
                          <div className="row gap-4" style={{ justifyContent: 'flex-end' }}>
                            {area.status === 'pending_approval' ? (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => void approve(area)}
                              >
                                <Icon name="check" size={14} /> Approve
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="icon-btn"
                              title="Assign reps"
                              aria-label={`Assign reps to ${area.name}`}
                              onClick={() => setAssigning(area)}
                            >
                              <Icon name="users" size={15} />
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              title="Edit"
                              aria-label={`Edit ${area.name}`}
                              onClick={() => setEditing(area)}
                            >
                              <Icon name="edit" size={15} />
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              title="Merge into another area"
                              aria-label={`Merge ${area.name}`}
                              onClick={() => setMerging(area)}
                            >
                              <Icon name="merge" size={15} />
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              title={`Add sub-area under ${area.name}`}
                              aria-label={`Add sub-area under ${area.name}`}
                              onClick={() => { setDefaultParentId(area.id); setEditing('new'); }}
                            >
                              <Icon name="plus" size={15} />
                            </button>
                            {area.status !== 'archived' ? (
                              <button
                                type="button"
                                className="icon-btn danger"
                                title="Archive"
                                aria-label={`Archive ${area.name}`}
                                onClick={() => void archive(area)}
                              >
                                <Icon name="trash" size={15} />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <AreaDialog
          area={editing === 'new' ? null : editing}
          onClose={() => { setEditing(null); setDefaultParentId(null); }}
          onSaved={() => { setEditing(null); setDefaultParentId(null); void load(); }}
          allAreas={(areas ?? []).filter((a) => a.status !== 'archived')}
          beatLevelNames={settings?.beatLevelNames ?? ['Area']}
          defaultParentId={defaultParentId}
        />
      ) : null}

      {merging ? (
        <MergeDialog
          source={merging}
          areas={(areas ?? []).filter((a) => a.id !== merging.id && a.status === 'active')}
          onClose={() => setMerging(null)}
          onMerged={() => { setMerging(null); void load(); }}
        />
      ) : null}

      {assigning ? (
        <AssignUsersDrawer
          area={assigning}
          onClose={() => setAssigning(null)}
          onSaved={() => { setAssigning(null); void load(); }}
        />
      ) : null}
    </>
  );
}

function AreaDialog({
  area, onClose, onSaved, allAreas, beatLevelNames, defaultParentId,
}: {
  area: Area | null;
  onClose: () => void;
  onSaved: () => void;
  allAreas: Area[];
  beatLevelNames: string[];
  defaultParentId?: string | null;
}) {
  const toast = useToast();
  const [name, setName] = useState(area?.name ?? '');
  const [code, setCode] = useState(area?.code ?? '');
  const [city, setCity] = useState(area?.city ?? '');
  const [parentId, setParentId] = useState(area?.parentId ?? defaultParentId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Exclude self + descendants from parent options to prevent circular refs
  const parentOptions = useMemo(() => {
    if (!area) return allAreas;
    const excluded = new Set<string>([area.id]);
    let frontier = [area.id];
    while (frontier.length) {
      const next: string[] = [];
      for (const pid of frontier) {
        for (const a of allAreas) {
          if (a.parentId === pid && !excluded.has(a.id)) {
            excluded.add(a.id);
            next.push(a.id);
          }
        }
      }
      frontier = next;
    }
    return allAreas.filter((a) => !excluded.has(a.id));
  }, [area, allAreas]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string | null> = { name: name.trim() };
      if (code.trim()) body.code = code.trim();
      if (city.trim()) body.city = city.trim();
      body.parentId = parentId || null;

      if (area) await api.patch(`/customers/routes/${area.id}`, body);
      else await api.post('/customers/routes', body);
      toast.success(area ? 'Area updated.' : `${body.name} added.`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the area.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={area ? 'Edit area' : (defaultParentId ? 'New sub-area' : 'New area')}
      subtitle={!area && defaultParentId
        ? `Under ${allAreas.find((a) => a.id === defaultParentId)?.name ?? 'parent area'}`
        : undefined}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || name.trim().length < 2}
            onClick={() => void save()}
          >
            {busy ? <span className="spinner" /> : null}
            {area ? 'Save' : 'Add area'}
          </button>
        </>
      )}
    >
      <div className="col gap-16">
        {error ? <Banner kind="danger">{error}</Banner> : null}
        <TextInput
          label="Area name"
          value={name}
          autoFocus
          maxLength={120}
          placeholder="Lajpat Nagar Market"
          hint="What a rep would call this beat out loud"
          onChange={(e) => setName(e.target.value)}
        />
        <div className="row gap-12" style={{ alignItems: 'flex-start' }}>
          <TextInput
            label="Code (optional)"
            value={code}
            maxLength={20}
            placeholder="DEL-LN-01"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <TextInput
            label="City (optional)"
            value={city}
            maxLength={80}
            placeholder="New Delhi"
            onChange={(e) => setCity(e.target.value)}
          />
        </div>
        <Select
          label="Parent area (optional)"
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
        >
          <option value="">— None (top level)</option>
          {treeOrder(parentOptions).map((a) => (
            <option key={a.id} value={a.id}>
              {'\u00A0\u00A0'.repeat(a.depth)}{a.name}
              {' '}({levelName(beatLevelNames, a.depth)})
            </option>
          ))}
        </Select>
      </div>
    </Modal>
  );
}

function MergeDialog({
  source, areas, onClose, onMerged,
}: { source: Area; areas: Area[]; onClose: () => void; onMerged: () => void }) {
  const toast = useToast();
  const [targetId, setTargetId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const target = areas.find((a) => a.id === targetId);

  async function merge() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ ok: boolean; customersMoved: number }>(
        '/customers/routes/merge',
        { sourceId: source.id, targetId },
      );
      toast.success(
        `${result.customersMoved} customer${result.customersMoved === 1 ? '' : 's'} moved to ${target?.name}.`,
      );
      onMerged();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not merge.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Merge ${source.name}`}
      subtitle="For when the same beat got entered twice under different names."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !targetId}
            onClick={() => void merge()}
          >
            {busy ? <span className="spinner" /> : null}
            Merge
          </button>
        </>
      )}
    >
      <div className="col gap-16">
        {error ? <Banner kind="danger">{error}</Banner> : null}

        <Select
          label="Move everything into"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
        >
          <option value="">Choose an area</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}{a.city ? ` — ${a.city}` : ''}
            </option>
          ))}
        </Select>

        {target ? (
          <Banner kind="warning">
            {source.customerCount ?? 0} customer{(source.customerCount ?? 0) === 1 ? '' : 's'}{' '}
            and every rep assignment move from <strong>{source.name}</strong> to{' '}
            <strong>{target.name}</strong>. {source.name} is then archived.
            This cannot be undone in one click.
          </Banner>
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Assign Users Drawer
// ---------------------------------------------------------------------------

function AssignUsersDrawer({
  area, onClose, onSaved,
}: { area: Area; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [allUsers, setAllUsers] = useState<TeamMember[] | null>(null);
  const [assigned, setAssigned] = useState<Set<string> | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep a snapshot of the original set so we know if anything changed.
  const [original, setOriginal] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      try {
        const [users, assignments] = await Promise.all([
          api.get<TeamMember[]>('/users', { status: 'active' }),
          api.get<AssignedUser[]>(`/customers/routes/${area.id}/assignments`),
        ]);
        if (cancelled) return;
        setAllUsers(users);
        const ids = new Set(assignments.map((a) => a.id));
        setAssigned(ids);
        setOriginal(new Set(ids));
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load team.');
          onClose();
        }
      }
    }

    void fetch();
    return () => { cancelled = true; };
  }, [area.id, toast, onClose]);

  const toggle = (userId: string) => {
    setAssigned((prev) => {
      if (!prev) return prev;
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const dirty = useMemo(() => {
    if (!assigned) return false;
    if (assigned.size !== original.size) return true;
    for (const id of assigned) if (!original.has(id)) return true;
    return false;
  }, [assigned, original]);

  const filtered = useMemo(() => {
    if (!allUsers) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return allUsers;
    return allUsers.filter((u) =>
      u.fullName.toLowerCase().includes(needle)
      || u.email?.toLowerCase().includes(needle)
      || u.employeeCode?.toLowerCase().includes(needle)
      || u.designation?.toLowerCase().includes(needle),
    );
  }, [allUsers, search]);

  async function save() {
    if (!assigned) return;
    setBusy(true);
    setError(null);
    try {
      await api.put(`/customers/routes/${area.id}/assignments`, {
        userIds: [...assigned],
      });
      toast.success(
        assigned.size
          ? `${assigned.size} rep${assigned.size === 1 ? '' : 's'} assigned to ${area.name}.`
          : `Cleared — every rep can now use ${area.name}.`,
      );
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save assignments.');
      setBusy(false);
    }
  }

  const assignedCount = assigned?.size ?? 0;

  return (
    <Drawer
      title={`Assign reps — ${area.name}`}
      subtitle={
        assignedCount
          ? `${assignedCount} rep${assignedCount === 1 ? '' : 's'} assigned`
          : 'No restriction — every rep can use this area'
      }
      onClose={onClose}
      width="min(420px, 96vw)"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !dirty}
            onClick={() => void save()}
          >
            {busy ? <span className="spinner" /> : null}
            Save
          </button>
        </>
      )}
    >
      {!allUsers || !assigned ? (
        <PageLoading rows={4} />
      ) : (
        <div className="col gap-8">
          {error ? <Banner kind="danger">{error}</Banner> : null}

          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name, email or code"
          />

          <p className="small faint" style={{ margin: '4px 0 0' }}>
            {assigned.size
              ? 'Only checked reps can work this area.'
              : 'Nobody is checked — every active rep can use this area.'}
          </p>

          <div className="col gap-2" style={{ marginTop: 4 }}>
            {filtered.length === 0 ? (
              <p className="faint" style={{ padding: '20px 0', textAlign: 'center' }}>
                No team members match &ldquo;{search}&rdquo;
              </p>
            ) : (
              filtered.map((user) => (
                <label
                  key={user.id}
                  className="assign-user-row"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px',
                    borderRadius: 8, cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={assigned.has(user.id)}
                    onChange={() => toggle(user.id)}
                    style={{ width: 16, height: 16, accentColor: 'var(--brand, #F15E22)' }}
                  />
                  <span
                    className="avatar-sm"
                    style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: 'var(--surface-2, #f3f4f6)',
                      color: 'var(--ink-muted, #52565e)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 600, flexShrink: 0,
                    }}
                  >
                    {user.avatarUrl
                      ? <img src={user.avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                      : initialsOf(user.fullName)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 500, fontSize: 14, lineHeight: 1.3 }}>
                      {user.fullName}
                    </span>
                    <span className="faint small" style={{ display: 'block', lineHeight: 1.3 }}>
                      {ROLE_LABEL[user.role]}
                      {user.designation ? ` · ${user.designation}` : ''}
                      {user.employeeCode ? ` · ${user.employeeCode}` : ''}
                    </span>
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
